import { useSyncExternalStore } from 'react';

/** Chromium-only event, not in lib.dom yet. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

/**
 * Called once from main.tsx, before React renders: the browser can fire beforeinstallprompt right
 * after load, long before the user ever opens /instalar, and it never fires it again.
 */
export function captureInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep it for our own button instead of letting the browser show its mini-infobar.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `canPrompt` is false when the browser doesn't support it or the app is already installed. */
export function useInstallPrompt() {
  const prompt = useSyncExternalStore(subscribe, () => deferredPrompt);

  async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!prompt) return 'unavailable';
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // A prompt can only be used once.
    deferredPrompt = null;
    emit();
    return outcome;
  }

  return { canPrompt: prompt !== null, promptInstall };
}
